/**
 * Portal → app sign-in hand-off (#162, #242).
 *
 * Portal tiles open each app with `?login_hint=<upn>`. An MSAL app must:
 *  1. read the hint and strip it from the address bar (takeLoginHint), and
 *  2. pick the account that hint names — never simply the first cached one
 *     (chooseAccount). The MSAL cache is persistent (localStorage), so on a
 *     shared device the first cached account can be the PREVIOUS user, and the
 *     app would call its API as them.
 *
 * Passing the hint to loginRedirect is what stops Entra showing its account
 * picker — verified 2026-09-24: the apps that pass it did not prompt a second
 * time from the portal; GM Report, which didn't, did.
 */

interface AccountLike {
  username?: string | null;
}

/** Read `login_hint` from the URL and remove it from the address bar. */
export function takeLoginHint(): string | undefined {
  const params = new URLSearchParams(window.location.search);
  const loginHint = params.get('login_hint') ?? undefined;
  if (loginHint) {
    params.delete('login_hint');
    const qs = params.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash,
    );
  }
  return loginHint;
}

/**
 * Which cached account the app should run as, or undefined when it must sign
 * in. In order:
 *  - the account a sign-in redirect just returned — the hint is gone from the
 *    URL by then, so without this a new user would fall back to the old one;
 *  - with a hint, only the cached account it names;
 *  - with no hint (opened directly, or the portal, which never gets one): the
 *    account last chosen in this app on this device (MSAL keeps the active
 *    account in localStorage), else the only cached account. With several
 *    cached and none chosen before, undefined, so the caller asks (#313).
 *    Never simply the first cached one — on a shared device that is whoever
 *    happened to sign in first.
 */
export function chooseAccount<T extends AccountLike>(options: {
  accounts: readonly T[];
  loginHint?: string;
  redirectAccount?: T | null;
  /** MSAL's persisted active account (getActiveAccount()). */
  activeAccount?: T | null;
}): T | undefined {
  const { accounts, loginHint, redirectAccount, activeAccount } = options;
  if (redirectAccount) return redirectAccount;
  if (loginHint) {
    const wanted = loginHint.toLowerCase();
    return accounts.find((a) => a.username?.toLowerCase() === wanted);
  }
  const lastUsed = activeAccount?.username?.toLowerCase();
  if (lastUsed) {
    const cached = accounts.find((a) => a.username?.toLowerCase() === lastUsed);
    if (cached) return cached;
  }
  return accounts.length === 1 ? accounts[0] : undefined;
}
